const HOLD_DURATION = 2500;

const setSlideDistances = (slides, activeIndex) => {
  slides.forEach((slide, index) => {
    slide.dataset.distance = String(Math.min(Math.abs(index - activeIndex), 3));
  });
};

const initializeStatsSlider = (slider) => {
  const track = slider.querySelector('[data-stats-slider-track]');
  const sourceSlides = track
    ? Array.from(track.querySelectorAll(':scope > [data-stat-slide]'))
    : [];

  if (!track || sourceSlides.length < 2) return;

  sourceSlides.forEach((slide) => {
    const before = slide.cloneNode(true);
    const after = slide.cloneNode(true);
    before.setAttribute('aria-hidden', 'true');
    after.setAttribute('aria-hidden', 'true');
    track.insertBefore(
      before,
      track.children[sourceSlides.indexOf(slide)] ?? null,
    );
    track.append(after);
  });

  const slides = Array.from(
    track.querySelectorAll(':scope > [data-stat-slide]'),
  );
  const count = sourceSlides.length;
  let activeIndex = count;
  let timer;
  let isHovered = false;
  let hasFocus = false;
  let isVisible = true;
  let resetFrame;
  let resetPaintFrame;

  const restoreTransitions = () => {
    window.cancelAnimationFrame(resetFrame);
    window.cancelAnimationFrame(resetPaintFrame);
    resetFrame = requestAnimationFrame(() => {
      resetPaintFrame = requestAnimationFrame(() => {
        track.classList.remove('is-resetting');
      });
    });
  };

  const position = (animate = true) => {
    const activeSlide = slides[activeIndex];
    if (!activeSlide) return;

    if (!animate) track.classList.add('is-resetting');
    const offset =
      slider.clientWidth / 2 -
      (activeSlide.offsetLeft + activeSlide.offsetWidth / 2);
    track.style.transform = `translate3d(${offset}px, -50%, 0)`;
    setSlideDistances(slides, activeIndex);

    if (!animate) {
      // Commit the identical logical position while transitions are disabled,
      // then restore animation only after the browser has painted that reset.
      track.getBoundingClientRect();
      restoreTransitions();
    }
  };

  const canRun = () => !isHovered && !hasFocus && isVisible && !document.hidden;

  const schedule = () => {
    window.clearTimeout(timer);
    if (!canRun()) return;
    timer = window.setTimeout(() => {
      activeIndex += 1;
      position();
    }, HOLD_DURATION);
  };

  track.addEventListener('transitionend', (event) => {
    if (event.propertyName !== 'transform') return;
    if (activeIndex >= count * 2) {
      activeIndex = count;
      position(false);
    }
    schedule();
  });

  slider.addEventListener('mouseenter', () => {
    isHovered = true;
    window.clearTimeout(timer);
  });
  slider.addEventListener('mouseleave', () => {
    isHovered = false;
    schedule();
  });
  slider.addEventListener('focusin', () => {
    hasFocus = true;
    window.clearTimeout(timer);
  });
  slider.addEventListener('focusout', (event) => {
    if (slider.contains(event.relatedTarget)) return;
    hasFocus = false;
    schedule();
  });

  document.addEventListener('visibilitychange', schedule);
  window.addEventListener('resize', () => position(false));

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible) schedule();
      else window.clearTimeout(timer);
    });
    observer.observe(slider);
  }

  position(false);
  schedule();
};

export const initializeStatsSliders = () => {
  document
    .querySelectorAll('[data-stats-slider]')
    .forEach(initializeStatsSlider);
};
